import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';
import ShareButton from '../components/ShareButton';
import { MdHowToVote } from 'react-icons/md';

function PublicPollPage({ isCustomPoll = false }) {
  const { pollId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [poll, setPoll] = useState(null);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [voting, setVoting] = useState(false);

  // Generate OG meta tag content
  const getOGDescription = () => {
    if (!poll) return 'Share your voice on CIVOTE - the civic polling platform';
    
    const totalResponses = (poll.yesCount || 0) + (poll.noCount || 0);
    if (totalResponses > 0) {
      const yesPercent = Math.round((poll.yesCount / totalResponses) * 100);
      const noPercent = 100 - yesPercent;
      return `${yesPercent}% Yes, ${noPercent}% No (${totalResponses} votes) - Vote now on CIVOTE!`;
    }
    return 'Be the first to vote on this poll! Share your opinion on CIVOTE.';
  };

  const getOGTitle = () => {
    if (!poll) return 'CIVOTE Poll';
    return poll.question || 'CIVOTE Poll';
  };

  const getOGUrl = () => {
    const baseUrl = window.location.origin;
    return isCustomPoll 
      ? `${baseUrl}/poll/custom/${pollId}`
      : `${baseUrl}/poll/${pollId}`;
  };

  const getOGImage = () => {
    return `${API_URL}/api/og/poll/${pollId}/image`;
  };

  useEffect(() => {
    loadPollData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollId, isCustomPoll]);

  const loadPollData = async () => {
    try {
      setLoading(true);
      
      // Different API endpoints for system vs custom polls
      const pollEndpoint = isCustomPoll 
        ? `${API_URL}/api/public/custom-polls/${pollId}`
        : `${API_URL}/api/public/polls/${pollId}`;
      
      const [pollRes, tagsRes] = await Promise.all([
        axios.get(pollEndpoint),
        axios.get(`${API_URL}/api/tags`)
      ]);
      setPoll(pollRes.data);
      setTags(tagsRes.data);
    } catch (err) {
      console.error('Failed to load poll:', err);
      setError('Poll not found or has been deleted.');
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (answer) => {
    if (!user) {
      const redirectPath = isCustomPoll ? `/poll/custom/${pollId}` : `/poll/${pollId}`;
      navigate('/', { state: { redirectTo: redirectPath } });
      return;
    }

    setVoting(true);
    try {
      const voteEndpoint = isCustomPoll
        ? `${API_URL}/api/custom-polls/${pollId}/respond`
        : `${API_URL}/api/polls/${pollId}/respond`;
      
      await axios.post(voteEndpoint, { answer });
      // Refresh poll data to show updated state
      await loadPollData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit vote');
    } finally {
      setVoting(false);
    }
  };

  const pollTags = tags.filter(tag => poll?.tags?.includes(tag.tagId));

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-civote flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-civote flex items-center justify-center p-4">
        <div className="bg-white rounded-card shadow-card max-w-md w-full p-8 text-center">
          <Logo size="large" />
          <h1 className="text-2xl font-bold text-gray-800 mt-4 font-rubik">Poll Not Found</h1>
          <p className="text-gray-600 mt-2 font-rubik">{error}</p>
          <Link 
            to="/" 
            className="btn-primary mt-6 inline-block"
            data-testid="go-home-button"
          >
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-civote">
      {/* Open Graph Meta Tags for Social Sharing */}
      <Helmet>
        <title>{getOGTitle()} | CIVOTE</title>
        <meta name="description" content={getOGDescription()} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={getOGUrl()} />
        <meta property="og:title" content={getOGTitle()} />
        <meta property="og:description" content={getOGDescription()} />
        <meta property="og:image" content={getOGImage()} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="CIVOTE" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={getOGUrl()} />
        <meta name="twitter:title" content={getOGTitle()} />
        <meta name="twitter:description" content={getOGDescription()} />
        <meta name="twitter:image" content={getOGImage()} />
      </Helmet>

      {/* Header */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to={user ? '/home' : '/'}>
              <Logo />
            </Link>
            <h1 className="text-xl font-bold text-gray-800 font-rubik">CIVOTE Poll</h1>
          </div>
          <div className="flex items-center space-x-3">
            {user ? (
              <>
                <Link 
                  to="/polls" 
                  className="btn-tertiary text-sm px-4 py-2"
                  data-testid="all-polls-button"
                >
                  All Polls
                </Link>
                <Link 
                  to="/profile" 
                  className="btn-secondary text-sm px-4 py-2"
                  data-testid="profile-button"
                >
                  Profile
                </Link>
              </>
            ) : (
              <>
                <Link 
                  to="/" 
                  className="btn-primary text-sm px-4 py-2"
                  data-testid="login-button"
                >
                  Login
                </Link>
                <Link 
                  to="/" 
                  className="btn-secondary text-sm px-4 py-2"
                  data-testid="register-button"
                >
                  Register
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Poll Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="card-container" data-testid="public-poll-card">
          {/* Poll Header */}
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-800 font-rubik" data-testid="poll-question">
                {poll.question}
              </h2>
              {pollTags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {pollTags.map(tag => (
                    <span
                      key={tag.tagId}
                      className="tag-pill text-white font-rubik"
                      style={{ backgroundColor: tag.color }}
                    >
                      {tag.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <ShareButton 
              pollId={pollId} 
              pollQuestion={poll.question} 
              isCustomPoll={isCustomPoll}
            />
          </div>

          {/* Voting Section */}
          {user ? (
            // Logged in user - show voting or result
            poll.userAnswered ? (
              <div className="bg-green-50 border border-primary rounded-card p-4" data-testid="user-answer-display">
                <p className="text-primary font-medium font-rubik flex items-center gap-2">
                  <MdHowToVote className="text-xl" />
                  Your answer: <span className="font-bold capitalize">{poll.userAnswer}</span>
                </p>
                <Link 
                  to="/polls" 
                  className="text-sm text-tertiary hover:text-primary font-medium mt-2 inline-block"
                >
                  View all polls →
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-600 font-rubik">Cast your vote:</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleVote('yes')}
                    disabled={voting}
                    className="flex-1 btn-primary disabled:opacity-50"
                    data-testid="vote-yes-button"
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => handleVote('no')}
                    disabled={voting}
                    className="flex-1 btn-danger disabled:opacity-50"
                    data-testid="vote-no-button"
                  >
                    No
                  </button>
                </div>
              </div>
            )
          ) : (
            // Not logged in - prompt to register
            <div className="bg-blue-50 border border-secondary rounded-card p-6 text-center" data-testid="login-prompt">
              <MdHowToVote className="text-4xl text-secondary mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-800 font-rubik mb-2">
                Join CIVOTE to Vote
              </h3>
              <p className="text-gray-600 font-rubik mb-4">
                Create a free account to participate in this poll and see how others voted.
              </p>
              <div className="flex gap-3 justify-center">
                <Link 
                  to="/" 
                  className="btn-primary px-6"
                  data-testid="register-to-vote-button"
                >
                  Register to Vote
                </Link>
                <Link 
                  to="/" 
                  className="btn-tertiary px-6"
                  data-testid="login-to-vote-button"
                >
                  Login
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Additional Info */}
        <div className="mt-6 text-center">
          <p className="text-gray-500 text-sm font-rubik">
            Powered by <span className="font-semibold text-primary">CIVOTE</span> - Your voice matters
          </p>
        </div>
      </div>
    </div>
  );
}

export default PublicPollPage;
